export const VIDEO_FRAME_CAPTURE_TIMEOUT_MS = 15000
const JPEG_QUALITY = 0.92
const ALPHA_OFFSET = 3
const RGBA_CHANNELS = 4

// EXPL: drawImage() silently draws nothing when the browser has no decoded frame to hand over, and a
// transparent canvas encoded as JPEG comes out solid black - so an all-transparent canvas is a failed capture.
function isBlank(ctx: CanvasRenderingContext2D, width: number, height: number) {
  const data = ctx.getImageData(0, 0, width, height).data
  for (let i = ALPHA_OFFSET; i < data.length; i += RGBA_CHANNELS) {
    if (data[i] !== 0) {
      return false
    }
  }
  return true
}

/**
 * Loads a video from a URL, seeks to the specified time, captures a frame,
 * and returns it as a base64-encoded data URL (image/jpeg).
 *
 * Requirements:
 * - The video URL must be CORS-accessible (object URLs of local files work too).
 * - Works with direct video files (.mp4, .webm, etc.).
 *
 * Rejects when the video fails to load, the browser cannot decode its frames, the captured frame is blank,
 * or no frame is captured within VIDEO_FRAME_CAPTURE_TIMEOUT_MS.
 *
 * @param url       - The video URL to capture a thumbnail from.
 * @param seekTime  - The time (in seconds) to seek to before capturing. Defaults to 0.
 * @returns A Promise resolving to a base64 data URL string (e.g. "data:image/jpeg;base64,...").
 */
export function getVideoThumbnailFromUrl(url: string, seekTime = 0): Promise<string> {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video')
    video.crossOrigin = 'anonymous'
    video.preload = 'metadata'
    video.muted = true

    const cleanup = () => {
      clearTimeout(timeout)
      video.removeEventListener('loadedmetadata', onLoadedMetadata)
      video.removeEventListener('seeked', onSeeked)
      video.removeEventListener('error', onError)
      video.src = ''
      video.load()
    }

    const fail = (error: Error) => {
      cleanup()
      reject(error)
    }

    const onSeeked = () => {
      try {
        if (!video.videoWidth || !video.videoHeight) {
          fail(new Error('Video has no decodable frames'))
          return
        }
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth
        canvas.height = video.videoHeight
        const ctx = canvas.getContext('2d')
        if (!ctx) {
          fail(new Error('Failed to get 2D canvas context'))
          return
        }
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        if (isBlank(ctx, canvas.width, canvas.height)) {
          fail(new Error('Captured video frame is blank'))
          return
        }
        const base64 = canvas.toDataURL('image/jpeg', JPEG_QUALITY)
        cleanup()
        resolve(base64)
      } catch (err) {
        fail(err as Error)
      }
    }

    const onError = () => {
      fail(new Error(`Failed to load video from URL: ${url}`))
    }

    // Once metadata is loaded we know the duration; then seek to the requested time.
    const onLoadedMetadata = () => {
      video.currentTime = Math.min(seekTime, video.duration)
    }

    const timeout = setTimeout(() => fail(new Error('Timed out capturing video frame')), VIDEO_FRAME_CAPTURE_TIMEOUT_MS)

    video.addEventListener('seeked', onSeeked)
    video.addEventListener('error', onError)
    video.addEventListener('loadedmetadata', onLoadedMetadata, { once: true })

    video.src = url
    video.load()
  })
}
